/**
 * Signed-out screen: one email field, no password, ever.
 */
import { messageOf } from '../api';
import { sendMagicLink } from '../auth';
import { APP_NAME } from '../config';
import { announce, el, looksLikeEmail, messageSlot, setBusy, setDocumentTitle } from '../ui';
import type { Teardown } from './view';

export function mountSignIn(
  host: HTMLElement,
  initialError: string | null,
  /** Where the emailed link should land, so a deep link is not lost here. */
  returnPath = '/',
): Teardown {
  setDocumentTitle('Sign in');

  const messages = messageSlot();
  const emailInput = el('input', {
    class: 'input',
    attrs: {
      id: 'email',
      type: 'email',
      name: 'email',
      inputmode: 'email',
      autocomplete: 'email',
      autocapitalize: 'off',
      spellcheck: 'false',
      placeholder: 'you@example.com',
      required: 'required',
    },
  });

  const submit = el('button', {
    class: 'button button-primary button-block',
    text: 'Email me a sign-in link',
    attrs: { type: 'submit' },
  });

  const form = el('form', {
    class: 'stack',
    attrs: { novalidate: 'novalidate' },
    children: [
      el('div', {
        class: 'field',
        children: [
          el('label', { class: 'label', text: 'Email address', attrs: { for: 'email' } }),
          emailInput,
        ],
      }),
      submit,
    ],
  });

  const card = el('section', {
    class: 'card card-narrow',
    children: [
      el('h1', { class: 'title', text: 'Sign in to review and sign your documents' }),
      el('p', {
        class: 'lede',
        text:
          'There is no password. Enter your email and we will send you a one-time link that signs you in.',
      }),
      messages.node,
      form,
      el('p', {
        class: 'fine-print',
        text: 'Use the same address the document was sent to, or we will not find it.',
      }),
    ],
  });

  const sent = (email: string): HTMLElement =>
    el('section', {
      class: 'card card-narrow',
      children: [
        el('h1', { class: 'title', text: 'Check your email' }),
        el('p', {
          class: 'lede',
          children: [
            'We sent a sign-in link to ',
            el('strong', { text: email }),
            '. It expires in 1 hour.',
          ],
        }),
        el('p', {
          class: 'fine-print',
          text:
            'Open the link on any device to continue. If it has not arrived in a minute, check your spam folder.',
        }),
        el('button', {
          class: 'button button-quiet',
          text: 'Use a different email',
          attrs: { type: 'button' },
          on: {
            click: () => {
              host.replaceChildren(card);
              messages.clear();
              emailInput.focus();
            },
          },
        }),
      ],
    });

  let submitting = false;
  const onSubmit = (event: Event): void => {
    event.preventDefault();
    if (submitting) return;

    const email = emailInput.value.trim();
    if (!looksLikeEmail(email)) {
      messages.show('error', 'Enter a valid email address, like you@example.com.');
      emailInput.focus();
      return;
    }

    submitting = true;
    messages.clear();
    setBusy(submit, true, 'Sending your link...');

    void sendMagicLink(email, returnPath)
      .then(() => {
        host.replaceChildren(sent(email));
        announce(`Sign-in link sent to ${email}. It expires in 1 hour.`);
      })
      .catch((err: unknown) => {
        messages.show(
          'error',
          messageOf(err, 'We could not send that link. Please check the address and try again.'),
        );
        emailInput.focus();
      })
      .finally(() => {
        submitting = false;
        if (submit.isConnected) setBusy(submit, false);
      });
  };

  form.addEventListener('submit', onSubmit);
  host.replaceChildren(card);

  if (initialError) messages.show('error', initialError);
  else announce(`${APP_NAME}. Sign in with your email address.`);

  return () => {
    form.removeEventListener('submit', onSubmit);
  };
}
